import { useState } from "react";
import { Radio, RadioGroup, Stack, Text, Box, Grid } from "@chakra-ui/react";
import Joi from "joi";

import Button from "components/Chakra/Button";
import { InitialUseCreateModalType, useCreateModal } from "../ModalsProvider";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import PhoneInput, { JoiPhoneValidator } from "components/Chakra/PhoneInput";
import AnimatedInput from "components/Chakra/AnimatedInput";
import LocationSuggest from "components/Chakra/LocationSuggest";
import CustomLink from "components/Chakra/CustomLink";
import { useInfoModal } from "modals/useInfoModal";
import useHelperCRM from "hooks/useHelperCRM";
import { useAlertModal } from "modals/useAlertModal";

export type PricePackageType = "BASIC" | "ADVANCED" | "CUSTOM";

const getPrice = (type: PricePackageType): string => {
  switch (type) {
    case "BASIC":
      return "4 000 Kč";
    case "ADVANCED":
      return "10 000 Kč";
    default:
      return "Na míru";
  }
};

type PricePackageMatterportModalProps = {
  type: PricePackageType;
  title: string;
};

type IFormValue = {
  type: PricePackageType;
  phone: { number: string; prefix: string };
  country: string;
  email: string;
};

const itemsPricePackage: Array<
  Omit<PricePackageMatterportModalProps, "price">
> = [
  {
    type: "BASIC",
    title: "Základní",
  },
  {
    type: "ADVANCED",
    title: "Rozšířený",
  },
  {
    type: "CUSTOM",
    title: "Na míru",
  },
];

const formSchema = Joi.object({
  type: Joi.string().required(),
  phone: JoiPhoneValidator(false),
  country: Joi.string().required().trim().max(33),
  email: Joi.string().email({ tlds: false }).required().trim(),
});

const PricePackageMatterportModal: React.FC<
  PricePackageMatterportModalProps
> = ({ type }) => {
  const [price, setPrice] = useState(getPrice(type));
  const { createOrUpdateUser } = useHelperCRM();
  const { openModal: openAlertModal } = useAlertModal();
  const { openModal: openInfoModal } = useInfoModal();
  const {
    registerWithError,
    handleSubmit,
    watch,
    errors,
    setValue,
    clearErrors,
    getValues,
    reset,
  } = useForm<IFormValue>(formSchema, {
    keepDataOnSuccess: true,
    defaultValues: {
      type,
      phone: { number: "", prefix: "+420" },
    },
  });

  const watchRadioValue = watch("type");
  const phoneNumber = watch("phone.number");
  const phonePrefix = watch("phone.prefix");

  const handleData: OnFormSubmitType<IFormValue> = async ({
    country,
    email,
  }) => {
    const userIdReq = await createOrUpdateUser(email, {
      email,
      offers: [],
      phone: phoneNumber.length > 0 ? phonePrefix + phoneNumber : "",
      leadMatterportAcceptFrom: "vizualni_prezentaca",
      leadMatterportLocationProperty: country,
      leadMatterportUtilityType: watchRadioValue,
      isLeadMatterport: true,
      consents: [],
    });

    reset();
    if (userIdReq) {
      return openAlertModal({
        animation: "send",
        title: "Úspěšně odesláno. Ozveme se vám.",
        submitText: "Zavřít",
      });
    }

    return openAlertModal({
      animation: "error",
      title:
        "Něco se pokazilo. Zkuste to prosím znovu nebo nás kontaktujte na e-mail podpora@ulovdomov.cz",
      submitText: "Chyba",
    });
  };

  return (
    <form
      onSubmit={handleSubmit(handleData)}
      style={{
        height: "100%",
      }}
    >
      <Stack direction={{ base: "column", md: "row" }} h="100%">
        <Stack
          w={{ md: "304px" }}
          backgroundColor="ud-grey-100"
          p={{ base: "32px 24px 24px", md: "48px 40px 40px" }}
        >
          <Text pb="16px" fontSize="24px" fontWeight={500}>
            Váš balíček
          </Text>
          <RadioGroup
            value={watchRadioValue}
            position="relative"
            display="flex"
            flexDirection="column"
            gap="10px"
            pb="24px"
          >
            {itemsPricePackage.map((item) => (
              <Box
                onClick={() => {
                  setValue("type", item.type);
                  setPrice(getPrice(item.type));
                }}
                key={item.type}
              >
                <Radio
                  value={item.type}
                  colorScheme="radioColor"
                  spacing="10px"
                >
                  <Text fontWeight="thin">{item.title}</Text>
                </Radio>
              </Box>
            ))}
          </RadioGroup>
          <Stack
            direction="row"
            justify="space-between"
            pt="24px"
            borderTop="1px solid"
            borderColor="ud-grey-300"
          >
            <Text>Cena</Text>
            <Text
              fontSize="20px"
              fontWeight={500}
              bgGradient="linear-gradient(86deg, #E74C3C 0%, #E98540 100%)"
              bgClip="text"
            >
              {price}
            </Text>
          </Stack>
        </Stack>
        <Grid
          //   flex={1}
          p={{ base: "24px", md: "48px 40px 40px" }}
          gridTemplateColumns={{ xl: "1fr 1fr" }}
          columnGap="24px"
          gridTemplateAreas={{
            base: `
                  "PHONE"
                  "EMAIL"
                  "COUNTRY"
                  "BOTTOM"
                `,
            md: `
                  "PHONE EMAIL"
                  "COUNTRY COUNTRY"
                  "BOTTOM BOTTOM"
                `,
          }}
        >
          <Stack gridArea="PHONE" flex={1} spacing={0}>
            <Text fontSize="16px" fontWeight={500}>
              Telefon
            </Text>
            <PhoneInput
              backgroundColor="ud-white"
              isLabel={false}
              error={errors.phone}
              setValue={setValue}
              selectBoxValue={phonePrefix}
              phoneNumberValue={phoneNumber}
              clearErrors={clearErrors}
              bgColor="inherit"
              placeholder="Vaše číslo"
            />
          </Stack>
          <Box gridArea="EMAIL">
            <Text fontSize="16px" fontWeight={500}>
              E-mail *
            </Text>
            <AnimatedInput
              {...registerWithError("email")}
              placeholder="Váš E-mail"
              required
            />
          </Box>
          <Box gridArea="COUNTRY" flex={1}>
            <Text fontSize="16px" fontWeight={500}>
              Kde se nemovistost nachází?*
            </Text>
            <LocationSuggest
              {...registerWithError("country")}
              placeholder="Obec"
              variant="DEFAULT"
              locationValue={getValues("country")}
              onLocationSelect={(loc) => {
                clearErrors("country");
                setValue("country", loc);
              }}
              onValueChange={(loc) => {
                clearErrors("country");
                setValue("country", loc);
              }}
              onClear={() => {
                setValue("country", "");
              }}
              sx={{
                borderWidth: "1px",
                borderColor: "ud-grey-300",
                borderStyle: "solid",
                _focusVisible: {
                  borderWidth: "1px",
                  borderColor: "ud-grey-500",
                  borderStyle: "solid",
                },
              }}
            />
            {errors.country && (
              <Text color="ud-primary" fontSize="12px">
                {errors.country.message}
              </Text>
            )}
          </Box>
          <Stack
            gridArea="BOTTOM"
            flex={1}
            spacing={0}
            mt="8px"
            display="grid"
            gap={{ base: "4px", md: "0" }}
            gridTemplateAreas={{
              base: `
                  "NOTIFICATION"
                  "NEWS"
                  "SUBMIT"
                `,
              md: `
                  "NOTIFICATION NOTIFICATION SUBMIT"
                  "NEWS NEWS SUBMIT"
                `,
            }}
            gridTemplateColumns={{ md: "1fr auto" }}
            gridTemplateRows={{ md: "auto auto" }}
          >
            <Text fontSize="12px" gridArea="NEWS">
              Odesláním formuláře souhlasíte se{" "}
              <Text
                as="span"
                fontSize="12px"
                textDecoration="underline"
                cursor="pointer"
                onClick={() =>
                  openInfoModal({
                    title: "Zpracování osobních údajů",
                    content: (
                      <>
                        <Text>
                          Vaše osobní údaje pečlivě chráníme a nebudeme je
                          nikomu dalšímu poskytovat. Vaše osobní údaje (jméno,
                          telefon, e-mail a údaje o nemovitosti) budeme my, tedy
                          společnost UlovDomov.cz s.r.o., IČO: 293 00 631, se
                          sídlem Lazaretní 925/9, 615 00 Brno, zpracovávat pouze
                          za účelem konzultace prodeje vaší nemovitosti a
                          informování vás o našich službách a produktech, v
                          souladu s platnou legislativou a zásadami ochrany
                          osobních údajů. Svůj souhlas se zpracováním osobních
                          údajů můžete kdykoliv odvolat – stačí nás kontaktovat
                          na{" "}
                          <CustomLink
                            href="mailto:info@ulovdomov.cz"
                            sx={{
                              textDecoration: "underline",
                            }}
                            textDecoration="underline"
                            color="ud-gray-300"
                          >
                            info@ulovdomov.cz
                          </CustomLink>
                          . Podrobnější informace a o ochraně osobních údajů
                          naleznete{" "}
                          <CustomLink
                            to="/zpracovani-osobnich-udaju"
                            target="_blank"
                            sx={{
                              textDecoration: "underline",
                            }}
                            color="ud-gray-300"
                          >
                            zde
                          </CustomLink>
                          .
                        </Text>
                      </>
                    ),
                  })
                }
              >
                zpracováním osobních údajů
              </Text>
            </Text>
            <Button
              type="submit"
              width="fit-content"
              outline="none"
              borderRadius="full"
              justifySelf={{ base: "flex-end", md: "center" }}
              px="24px"
              gridArea="SUBMIT"
              bg="linear-gradient(86deg, #E74C3C 0%, #E98540 100%)"
              _focus={{
                bg: "linear-gradient(86deg, #b72211 0%, #f0701b 100%)",
              }}
              _hover={{
                bg: "linear-gradient(86deg, #b72211 0%, #f0701b 100%)",
              }}
            >
              Odeslat
            </Button>
          </Stack>
        </Grid>
      </Stack>
    </form>
  );
};

export const usePricePackageMatterportModal: InitialUseCreateModalType<
  PricePackageMatterportModalProps
> = () =>
  useCreateModal(
    "pricePackageMatterportModal",
    PricePackageMatterportModal as React.FC,
    null,
    null,
    {
      size: "4xl",
      variant: "noBodyPadding",
    },
  );
